import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useEncryptService } from "../../shared/hooks";
import { useDispatch, useSelector } from "react-redux";
import { setComplianceTaskData } from "../../components/socket/event-manager.slice";

import {
    Box,
    Drawer,
    IconButton,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
    ListItemIcon,
    Slider,
    Chip,
    useTheme,
    Collapse,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,

} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WhiteTooltip from "../../components/WhiteTooltip";
import ReactJson from "react-json-view";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";

import PassIcon from "../../assets/baseVersion/Pass.svg";
import FailIcon from "../../assets/baseVersion/Fail.svg";
import NotApplicableImg from "../../assets/baseVersion/NotApplicable.svg";

interface AddRuleDrawerProps {
    open: boolean;
    onClose: () => void;
    onRuleAdded?: () => void;
    editData?: any; // Added for draft editing
}

const MIN_INSTRUCTION_CHARS = 16;
const MAX_INSTRUCTION_CHARS = 2250;
const { doEncrypt } = useEncryptService();

const awsInstructionGuidelines = [
    "The request MUST be about AWS (Amazon Web Services) resources, services, or configurations.",
    "Do not request anything about other cloud providers (Azure, GCP, Oracle Cloud, IBM Cloud, etc.), general questions (politics, history, science, etc.), or non-AWS/unrelated topics.",
    "User requests must include ONLY read-only operations such as GET, List, Describe, Check, Validate, Verify, or Audit.",
];

const AddRuleDrawer: React.FC<AddRuleDrawerProps> = ({ open, onClose, onRuleAdded, editData }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [ruleType, setRuleType] = useState("Cloud");
    const [assetDropdownOpen, setAssetDropdownOpen] = useState(false);
    const assetOptions = [
        "All",
        "Router",
        "Switch",
        "Firewall",
        "Wireless",
        "Small Business",
    ];
    const [selectedAssets, setSelectedAssets] = useState<string[]>(assetOptions);
    const [instruction, setInstruction] = useState("");
    const [instructionError, setInstructionError] = useState("");
    const [accountId, setAccountId] = useState("");
    const [credId, setCredId] = useState("");
    const [credentials, setCredentials] = useState<any[]>([]);
    const [isFetchingCredentials, setIsFetchingCredentials] = useState(false);
    const [isCreatingCredential, setIsCreatingCredential] = useState(false);
    const [newCredName, setNewCredName] = useState("");
    const [newCredDescription, setNewCredDescription] = useState("");
    const [newCredAccessKey, setNewCredAccessKey] = useState("");
    const [newCredSecretKey, setNewCredSecretKey] = useState("");
    const [ruleName, setRuleName] = useState<any>();
    const [description, setDescription] = useState("");
    const [applicableAsset, setApplicableAsset] = useState();
    const [remediation, setRemediation] = useState("");
    const [severityValue, setSeverityValue] = useState(0.1);
    const [cvssVector, setCvssVector] = useState("");
    const [openSteps, setOpenSteps] = useState<Set<number>>(new Set([0]));
    const [isDefineRuleCompleted, setIsDefineRuleCompleted] = useState(false);
    const [isRulesTestCompleted, setIsRulesTestCompleted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeploying, setIsDeploying] = useState(false);
    const [ruleTestResults, setRuleTestResults] = useState<any[]>([]);
    const [complianceResults, setComplianceResults] = useState<any>(null);
    const [expandedTestIndex, setExpandedTestIndex] = useState<number | null>(null);
    const [proofExpanded, setProofExpanded] = useState<Record<string, boolean>>({});
    const complianceTaskData = useSelector((state: any) => state.eventManager.complianceTaskData);
    const reduxOrgId = useSelector((state: any) => state.UserLoginDetails?.organization_id);
    const organization_id = reduxOrgId || editData?.organization_id || editData?.org_id || localStorage.getItem("organization_id") || "";

    // Populate data when editData is provided
    useEffect(() => {
        if (open && editData) {
            setInstruction(editData.control_name || "");
            setRuleName(editData.user_input || "");
            setDescription(editData.description || "");
            setRemediation(editData.remediation || "");
            const editScore = typeof editData.cvss_score === "number" ? editData.cvss_score : editData.control_info?.cvss_score;
            if (typeof editScore === "number") {
                setSeverityValue(Math.max(editScore, 0.1));
            } else {
                setSeverityValue(0.1);
            }
            setCvssVector(editData.cvss_vector || editData.control_info?.cvss_vector || "");
            setRuleUuid(editData.rule_uuid || "");

            // Populate test results from details object if it exists
            if (editData.details) {
                const details = editData.details;
                const statusMap: Record<string, RuleTestStatus> = {
                    "success": "success",
                    "failed": "failed",
                    "not_applicable": "not_applicable",
                    "pass": "success", // Support both possible backends
                    "fail": "failed"
                };

                // Check if it's the new nested structure or old array/object structure
                if (details.ruleTestResults && Array.isArray(details.ruleTestResults)) {
                    // New nested structure
                    setRuleTestResults(details.ruleTestResults.map((res: any, idx: number) => ({
                        ...res,
                        id: res.id || `draft-${Date.now()}-${idx}`,
                        status: statusMap[res.status_icon] || res.status || "pending",
                        hasDetails: res.hasDetails ?? !!(res.proof || res.reason)
                    })));
                    if (details.compliance_results) {
                        setComplianceResults(details.compliance_results);
                    }
                } else {
                    // Legacy structure (array or single object)
                    const detailsArray = Array.isArray(details) ? details : [details];
                    const results = detailsArray.map((detail: any, idx: number) => ({
                        id: `draft-${Date.now()}-${idx}`,
                        command: detail.command || "",
                        status: statusMap[detail.status_icon] || "",
                        hasDetails: !!(detail.proof || detail.reason),
                        reason: detail.reason || "",
                        proof: detail.proof || null
                    }));
                    setRuleTestResults(results);
                }
            }

            // Populate agent and vendor info if available
            if (editData.agent_id) {
                setSelectedAgent(editData.agent_id);
                setSocketAgentId(editData.agent_id);
            }
            if (editData.vendor) {
                setSocketVendor(editData.vendor);
            }

            // Set completion states 
            setIsDefineRuleCompleted(true);
            setIsRulesTestCompleted(true);

            // Start at Step 0 but allow navigation to Step 1 & 2
            setOpenSteps(new Set([0, 1, 2]));
        } else if (open && !editData) {
            // Reset for "Add" mode — clear test history and related transient state
            setRuleName("");
            setDescription("");
            setRemediation("");
            setSeverityValue(0.1);
            setCvssVector("");
            setRuleUuid("");
            setInstruction("");
            setAccountId("");
            setCredId("");
            setIsCreatingCredential(false);
            setNewCredName("");
            setNewCredDescription("");
            setNewCredAccessKey("");
            setNewCredSecretKey("");
            setIsDefineRuleCompleted(false);
            setIsRulesTestCompleted(false);
            setOpenSteps(new Set([0]));

            // Clear transient test/display state so "Previous Responses" doesn't persist
            setTestHistory([]);
            setCurrentHistoryIndex(-1);
            setRuleTestResults([]);
            setExpandedTestIndex(null);
            setProofExpanded({});
            setSocketAgentId("");
            setSocketVendor("");
            setSelectedAgent("");
            setIsLoading(false);
            setIsDeploying(false);
            setIsCopied(false);
            setInstructionError("");
            setRuleNameError("");
            setDescriptionError("");
            setRemediationError("");

            // Clear any task data in the global store used for showing results
            try {
                dispatch(setComplianceTaskData(null));
            } catch (e) {
                // ignore if dispatch not ready — defensive
            }
        }
    }, [open, editData]);

    const [testHistory, setTestHistory] = useState<any[]>([]);
    const [currentHistoryIndex, setCurrentHistoryIndex] = useState<number>(-1);

    const [socketAgentId, setSocketAgentId] = useState<string>("");
    const [socketVendor, setSocketVendor] = useState<string>("");

    const [agents, setAgents] = useState<any[]>([]);

    const [selectedAgent, setSelectedAgent] = useState<string>("");
    const [isFetchingAgents, setIsFetchingAgents] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [showDraftConfirm, setShowDraftConfirm] = useState(false);
    const [ruleUuid, setRuleUuid] = useState("");
    const [ruleNameError, setRuleNameError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");
    const [remediationError, setRemediationError] = useState("");
    const isSavingDraftRef = useRef(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);

    const fetchCredentials = useCallback(async () => {
        setIsFetchingCredentials(true);
        try {
            const res = await axiosInstance.get(END_POINTS.CREDENTIAL_MGMT);
            const list = res?.data?.data?.cred_list || res?.data?.data || res?.data || [];
            setCredentials(Array.isArray(list) ? list : []);
        } catch (err) {
            console.error("Error fetching credentials:", err);
        } finally {
            setIsFetchingCredentials(false);
        }
    }, []);

    useEffect(() => {
        if (open) fetchCredentials();
    }, [open, fetchCredentials]);

    useEffect(() => {
        const fetchAgents = async () => {
            if (!open) return;
            setIsFetchingAgents(true);
            try {
                const response = await axiosInstance.get(END_POINTS.CLOUD_AGENT);
                if (response?.status === 200) {
                    const rawAgents = response.data?.data || response.data || [];
                    const agentsList = Array.isArray(rawAgents) ? rawAgents : [];
                    const activeAgents = agentsList.filter((agent: any) => {
                        const statusDisplay = String(agent?.status_display || "").trim().toLowerCase();
                        return statusDisplay === "active";
                    });
                    setAgents(activeAgents);
                    if (activeAgents.length > 0) {
                        setSelectedAgent(activeAgents[0].id);
                    }
                }
            } catch (error) {
                console.error("Error fetching agents:", error);
            } finally {
                setIsFetchingAgents(false);
            }
        };
        fetchAgents();
    }, [open]);

    const applyTestData = (data: any) => {
        if (!data) return;

        // Map test results
        if (data.compliance_results) {
            setComplianceResults(data.compliance_results);

            if (data.compliance_results.item) {
                const rawItem = data.compliance_results.item;
                const itemArray = Array.isArray(rawItem) ? rawItem : [rawItem];

                const mappedResults = itemArray.map((item: any, index: number) => {
                    let status: RuleTestStatus = "pending";
                    if (item.status === "pass") status = "success";
                    else if (item.status === "fail") status = "failed";
                    else if (item.status === "na" || item.status === "not_applicable") status = "not_applicable";

                    return {
                        id: `result-${index}-${Math.random()}`,
                        command: item.data?.Name || data.control_info?.control_name || data.control_name || "Test Result",
                        status,
                        hasDetails: !!item.data,
                        reason: item.reason || "",
                        proof: item.proof || item.data || null
                    };
                });
                setRuleTestResults(mappedResults);
            }
        }

        // Map Rule Details
        const newRuleName = data.control_info?.control_name || data.control_name;
        if (newRuleName) {
            setRuleName(newRuleName);
        }

        if (data.control_info?.description || data.description) {
            setDescription(data.control_info?.description || data.description);
        }

        if (data.control_info?.remediation || data.remediation) {
            setRemediation(data.control_info?.remediation || data.remediation);
        }

        const incomingScore = data.control_info?.cvss_score ?? data.cvss_score;
        if (incomingScore !== undefined && incomingScore !== null) {
            setSeverityValue(Math.max(Number(incomingScore), 0.1));
        }

        const incomingVector = data.control_info?.cvss_vector || data.cvss_vector;
        if (incomingVector) {
            setCvssVector(incomingVector);
        }

        if (data.agent_id) {
            setSocketAgentId(data.agent_id);
        }

        if (data.vendor) {
            setSocketVendor(data.vendor);
        }

        if (data.user_input) {
            setInstruction(data.user_input);
        }

        // Update UI state to show results
        setIsDefineRuleCompleted(true);
        setIsRulesTestCompleted(true);
    };

    const handleNavigate = (direction: 'prev' | 'next') => {
        if (testHistory.length === 0) return;

        const newIndex = direction === 'prev'
            ? Math.max(0, currentHistoryIndex - 1)
            : Math.min(testHistory.length - 1, currentHistoryIndex + 1);

        if (newIndex !== currentHistoryIndex) {
            setCurrentHistoryIndex(newIndex);
            applyTestData(testHistory[newIndex]);
        }
    };

    useEffect(() => {
        if (complianceTaskData && isLoading) {
            console.log("Processing complianceTaskData while isLoading is true:", complianceTaskData);

            // Create a copy with the instruction for history tracking
            const dataWithInstruction = { ...complianceTaskData, user_input: instruction };

            // Add to history if not already present
            setTestHistory(prev => {
                const alreadyExists = prev.some(h => JSON.stringify(h.compliance_results) === JSON.stringify(dataWithInstruction.compliance_results));
                if (alreadyExists) return prev;
                const newHistory = [...prev, dataWithInstruction];
                setCurrentHistoryIndex(newHistory.length - 1);
                return newHistory;
            });

            applyTestData(dataWithInstruction);
            setOpenSteps(prev => new Set(prev).add(1)); // Switch to "Rules Test" step

            // Reset loading state
            setIsLoading(false);
        }
    }, [complianceTaskData, isLoading, instruction]);
    const isDarkMode = theme.palette.mode === "dark";
    const drawerBackground = theme.palette.background.paper;
    const subtleSurfaceColor = isDarkMode ? "rgba(255, 255, 255, 0.04)" : "#F9FAFB";
    const headerBorderColor = theme.palette.divider;
    const secondaryTextColor = theme.palette.text.secondary;
    const nonAllAssets = assetOptions.filter((asset) => asset !== "All");
    const severityLevels = [
        { min: 0, max: 3, label: "Low", color: "#F2BF00" },
        { min: 3, max: 6, label: "Medium", color: "#E85800" },
        { min: 6, max: 8, label: "High", color: "#A7000F" },
        { min: 8, max: 10, label: "Critical", color: "#670004" },
    ];

    type RuleTestStatus = "success" | "failed" | "not_applicable" | "pending";

    const steps = [
        { key: "define", label: "Define Rule" },
        { key: "test", label: "Rules Test" },
        { key: "details", label: "Rules Details" },
    ] as const;


    const getStatusStyles = (status: RuleTestStatus) => {
        switch (status) {
            case "success":
                return {
                    color: "#0A7E42",
                    icon: <Box component="img" src={PassIcon} alt="success" sx={{ width: 20, height: 20 }} />,
                };
            case "failed":
                return {
                    color: "#C62828",
                    icon: <Box component="img" src={FailIcon} alt="failed" sx={{ width: 20, height: 20 }} />,
                };
            case "not_applicable":
                return {
                    color: "#6B7280",
                    icon: <Box component="img" src={NotApplicableImg} alt="NA" sx={{ width: 20, height: 20 }} />,
                };
            case "pending":
            default:
                return {

                    // icon: <CircularProgress size={18} sx={{ color: "#6B7280" }} />,
                };
        }
    };

    const isStepEnabled = (index: number) => {
        if (index === 0) return true;
        if (index === 1) return isDefineRuleCompleted;
        if (index === 2) return isRulesTestCompleted;
        return false;
    };

    const handleStepSelection = (index: number) => {
        if (!isStepEnabled(index)) return;
        setOpenSteps(prev => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const handleRunTests = async () => {
        if (!instruction || instruction.length < MIN_INSTRUCTION_CHARS) {
            setInstructionError(`Instruction must be at least ${MIN_INSTRUCTION_CHARS} characters.`);
            return;
        }

        if (!selectedAgent) {
            toast.error("Please select an active agent.");
            return;
        }

        if (credId === "__create_new__") {
            if (!newCredName || !newCredAccessKey || !newCredSecretKey || !accountId) {
                toast.error("Please fill all required fields for creating a credential and account ID.");
                return;
            }
            try {
                const credPayload = {
                    cred_name: newCredName.trim(),
                    description: newCredDescription.trim(),
                    credential_type: "aws",
                    loginParameter: "aws",
                    account_id: doEncrypt(accountId.trim()),
                    access_key: doEncrypt(newCredAccessKey.trim()),
                    secret_key: doEncrypt(newCredSecretKey.trim()),
                    encrypt: true,
                    global_cred: true,
                };
                const createResp = await axiosInstance.post(END_POINTS.CREDENTIAL_MGMT, credPayload);
                if (createResp.status === 200 || createResp.status === 201) {
                    const finalCredId = createResp.data?.data?.id || createResp.data?.data?.credential_id || createResp.data?.data?.user_credentials_id || createResp.data?.data?.credential?.id;
                    if (!finalCredId) {
                        toast.error("Credential created but ID not found in response.");
                        return;
                    }
                    setCredId(finalCredId);
                    setIsCreatingCredential(false);
                    fetchCredentials();

                    dispatch(setComplianceTaskData(null));
                    setIsLoading(true);
                    try {
                        const payload = {
                            user_input: (instruction || "").trim(),
                            agent_id: (selectedAgent || "").trim(),
                            account_id: (accountId || "").trim(),
                            cred_id: (finalCredId || "").trim()
                        };

                        console.log(payload, "ppppp");


                        const response = await axiosInstance.post(END_POINTS.RULEMANAGEMENT_TEST, payload);

                        if (response.status === 200 || response.status === 201) {
                            toast.success("Rule test initiated successfully.");
                            setIsDefineRuleCompleted(true);
                        } else {
                            toast.error("Failed to initiate rule test.");
                            setIsLoading(false);
                        }
                    } catch (error: any) {
                        console.error("Error testing rule:", error);
                        toast.error(error?.response?.data?.message || "An error occurred while testing the rule.");
                        setIsLoading(false);
                    }
                } else {
                    toast.error("Failed to create credential.");
                    return;
                }
            } catch (err) {
                console.error(err);
                toast.error("Error creating credential.");
                return;
            }
            return; // We have submitted everything
        }

        dispatch(setComplianceTaskData(null));
        setIsLoading(true);
        try {
            const payload = {
                user_input: (instruction || "").trim(),
                agent_id: (selectedAgent || "").trim(),
                account_id: (accountId || "").trim(),
                cred_id: (credId || "").trim(),
            };

            console.log(payload, "ppppp");


            const response = await axiosInstance.post(END_POINTS.RULEMANAGEMENT_TEST, payload);

            if (response.status === 200 || response.status === 201) {
                toast.success("Rule test initiated successfully.");
                setIsDefineRuleCompleted(true);
            } else {
                toast.error("Failed to initiate rule test.");
                setIsLoading(false);
            }
        } catch (error: any) {
            console.error("Error testing rule:", error);
            toast.error(error?.response?.data?.message || "An error occurred while testing the rule.");
            setIsLoading(false);
        }
    };

    const handleCreateRule = () => {
        setIsRulesTestCompleted(true);
        setOpenSteps(prev => new Set(prev).add(2));
    };

    const handleDeploy = async () => {
        setIsDeploying(true);
        try {
            const payload = {
                organization_id: (organization_id || "").trim(),
                account_id: (accountId || "").trim(),
                agent_id: (socketAgentId || selectedAgent || "").trim(),
                control_name: (ruleName || "").trim(),
                user_input: (instruction || "").trim(),
                description: (description || "").trim(),
                remediation: (remediation || "").trim(),
                cvss_vector: (cvssVector || complianceResults?.control_info?.cvss_vector || complianceTaskData?.control_info?.cvss_vector || editData?.cvss_vector || editData?.control_info?.cvss_vector || "").trim(),
                cvss_score: severityValue || complianceResults?.control_info?.cvss_score || complianceTaskData?.control_info?.cvss_score || editData?.cvss_score || editData?.control_info?.cvss_score || 0.1,
                vendor: (socketVendor || "AWS").trim(),
                rule_type: (editData?.rule_type || "custom").trim(),
                ...(ruleUuid ? { rule_uuid: (ruleUuid || "").trim() } : {}),
                details: {
                    compliance_results: complianceResults || complianceTaskData?.compliance_results || editData?.details?.compliance_results || {},
                    result_count: complianceResults?.result_count ?? complianceTaskData?.result_count ?? editData?.details?.result_count ?? ruleTestResults.length,
                    processing_completed_at: complianceResults?.processing_completed_at || complianceTaskData?.processing_completed_at || editData?.details?.processing_completed_at || "",
                    control_info: complianceResults?.control_info || complianceTaskData?.control_info || editData?.details?.control_info || {},
                    ruleTestResults: ruleTestResults.map((result) => ({
                        command: (result.command || "").trim(),
                        reason: (result.reason || "").trim(),
                        proof: typeof result.proof === "string" ? result.proof.trim() : (result.proof || ""),
                        status_icon: (result.status || "").trim(),
                    })),
                }
            };
            console.log(payload, "yyyyyyyyyyyyyyy");


            const response = await axiosInstance.post(END_POINTS.RULE_MANAGEMENT_DEPLOY, payload);

            if (response.status === 200 || response.status === 201) {
                toast.success("Rule deployed successfully.");
                setTestHistory([]); // Clear history after deployment
                onClose();
                if (onRuleAdded) {
                    onRuleAdded();
                }
            } else {
                toast.error("Failed to deploy rule.");
            }
        } catch (error: any) {
            console.error("Error deploying rule:", error);
            toast.error(error?.response?.data?.message || "An error occurred while deploying the rule.");
        } finally {
            setIsDeploying(false);
        }
    };

    const toggleTestDetails = (index: number, hasDetails?: boolean) => {
        if (!hasDetails) return;
        setExpandedTestIndex((prev) => (prev === index ? null : index));
    };

    const toggleProofSection = (id: string) => {
        setProofExpanded((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const getSeverityLabel = (value: number) => {
        const level = severityLevels.find(
            (l, index) => {
                if (index === severityLevels.length - 1) {
                    // Last level (Critical) should include max value
                    return value >= l.min && value <= l.max;
                }
                return value >= l.min && value < l.max;
            }
        ) || severityLevels[0];
        return level.label;
    };

    const getSeverityColor = (value: number) => {
        const level = severityLevels.find(
            (l, index) => {
                if (index === severityLevels.length - 1) {
                    // Last level (Critical) should include max value
                    return value >= l.min && value <= l.max;
                }
                return value >= l.min && value < l.max;
            }
        ) || severityLevels[0];
        return level.color;
    };

    const handleAssetChange = (asset: string) => {
        if (asset === "All") {
            setSelectedAssets(assetOptions);
            return;
        }

        setSelectedAssets((prev) => {
            const normalizedPrev = prev.includes("All") ? [...nonAllAssets] : prev;
            const next = normalizedPrev.includes(asset)
                ? normalizedPrev.filter((a) => a !== asset)
                : [...normalizedPrev, asset];

            if (next.length === nonAllAssets.length) {
                return assetOptions;
            }

            return next;
        });
    };

    const handleInstructionInputChange = (value: string) => {
        setInstruction(value);
        const charCount = value.length;

        if (charCount === 0) {
            setInstructionError("");
            return;
        }

        if (charCount < MIN_INSTRUCTION_CHARS) {
            setInstructionError(
                `Add ${MIN_INSTRUCTION_CHARS - charCount} more characters (minimum ${MIN_INSTRUCTION_CHARS}).`
            );
        } else if (charCount > MAX_INSTRUCTION_CHARS) {
            setInstructionError(
                `Remove ${charCount - MAX_INSTRUCTION_CHARS} characters to stay within ${MAX_INSTRUCTION_CHARS}.`
            );
        } else {
            setInstructionError("");
        }
    };

    // Rule Name handler
    const handleRuleNameChange = (value: string) => {
        setRuleName(value);
        const trimmed = (value || "").toString().trim();
        if (trimmed.length === 0) {
            setRuleNameError("Rule name is required.");
        } else if (trimmed.length < 6) {
            setRuleNameError(`Add ${6 - trimmed.length} more characters (minimum 6).`);
        } else if (trimmed.length > 256) {
            setRuleNameError(`Remove ${trimmed.length - 256} characters (maximum 256).`);
        } else {
            setRuleNameError("");
        }
    };

    // Description handler
    const handleDescriptionChange = (value: string) => {
        setDescription(value);
        setDescriptionError((value || "").toString().trim().length === 0 ? "Description is required." : "");
    };

    // Remediation handler
    const handleRemediationChange = (value: string) => {
        setRemediation(value);
        setRemediationError((value || "").toString().trim().length === 0 ? "Remediation is required." : "");
    };

    const instructionCharCount = useMemo(
        () => instruction.length,
        [instruction]
    );
    const isDeployValid = useMemo(() => {
        const nameOk = (ruleName || "").toString().trim().length >= 6 && (ruleName || "").toString().trim().length <= 256;
        const descOk = (description || "").toString().trim().length > 0;
        const remOk = (remediation || "").toString().trim().length > 0;
        return nameOk && descOk && remOk;
    }, [ruleName, description, remediation]);

    const hasPendingResults = useMemo(() => {
        return ruleTestResults.some(res => res.status === "pending" || !["success", "failed", "not_applicable"].includes(res.status));
    }, [ruleTestResults]);

    const handleCopy = () => {
        navigator.clipboard.writeText(instruction);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };
    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={() => setShowDraftConfirm(true)}
            PaperProps={{
                sx: {
                    width: { xs: "100%", sm: "600px", md: "700px" },
                    maxWidth: "750px",
                    overflow: "hidden",
                    bgcolor: drawerBackground,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    backgroundColor: drawerBackground,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 2,
                        borderBottom: 1,
                        borderColor: headerBorderColor,
                        position: "relative",
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            fontSize: "18px",
                        }}
                    >
                        Add New Rule
                    </Typography>
                    <IconButton
                        onClick={() => setShowDraftConfirm(true)}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            color: secondaryTextColor,
                        }}
                    >
                        <CancelIcon />
                    </IconButton>
                </Box>

                {/* Scrollable Content */}
                <Box sx={{ overflowY: "auto", p: 2 }} className="scroll">
                    <Box>
                        {steps.map((step, index) => (
                            <Box key={step.key} sx={{ mb: 1 }}>
                                {/* Accordion Header */}
                                <Box
                                    onClick={() => handleStepSelection(index)}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        py: 1.2,
                                        cursor: isStepEnabled(index) ? "pointer" : "default",
                                        opacity: isStepEnabled(index) ? 1 : 0.5,
                                        userSelect: "none",
                                    }}
                                >
                                    {/* Numbered circle icon */}
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "13px",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                            backgroundColor: isStepEnabled(index)
                                                ? theme.palette.primary.main
                                                : theme.palette.action.disabledBackground,
                                            color: isStepEnabled(index)
                                                ? "#fff"
                                                : theme.palette.text.disabled,
                                        }}
                                    >
                                        {index + 1}
                                    </Box>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "13px",
                                            color: theme.palette.text.primary,
                                            flex: 1,
                                        }}
                                    >
                                        {step.label}
                                    </Typography>
                                    {step.key === "details" && isStepEnabled(index) && (
                                        <Box
                                            onClick={(e) => {
                                                if (openSteps.has(index)) return;
                                                e.stopPropagation();
                                                setOpenSteps((prev) => new Set(prev).add(index));
                                            }}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                cursor: openSteps.has(index) ? "default" : "pointer",
                                                color: openSteps.has(index)
                                                    ? theme.palette.action.disabled
                                                    : theme.palette.primary.main,
                                                mr: 1,
                                                opacity: openSteps.has(index) ? 0.6 : 1,
                                            }}
                                        >
                                            <EditIcon sx={{ fontSize: 16 }} />
                                            <Typography
                                                sx={{
                                                    fontSize: "13px",
                                                    fontWeight: 600,
                                                    color: "inherit",
                                                }}
                                            >
                                                Edit
                                            </Typography>
                                        </Box>
                                    )}
                                    {isStepEnabled(index) && (
                                        <ExpandMoreIcon
                                            sx={{
                                                transition: "transform 0.3s",
                                                transform: openSteps.has(index) ? "rotate(180deg)" : "rotate(0deg)",
                                                color: secondaryTextColor,
                                                fontSize: 20,
                                            }}
                                        />
                                    )}
                                </Box>
                                {/* Accordion Content */}
                                <Collapse in={openSteps.has(index)} unmountOnExit>
                                    <Box sx={{ pl: 5, pb: 2 }}>
                                        {step.key === "define" && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        gap: 3,
                                                        alignItems: "flex-start",
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: "12px",
                                                                fontWeight: "700",
                                                            }}
                                                        >
                                                            Type:
                                                        </Typography>
                                                        <RadioGroup
                                                            value={ruleType}
                                                            onChange={(e) =>
                                                                setRuleType(e.target.value)
                                                            }
                                                            row
                                                        >
                                                            <FormControlLabel
                                                                value="Cloud"
                                                                control={
                                                                    <Radio
                                                                        sx={{ color: secondaryTextColor, fontSize: "12px", fontWeight: 700 }}
                                                                    />
                                                                }
                                                                label="Cloud"
                                                            />
                                                        </RadioGroup>
                                                    </Box>

                                                    <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel id="agent-label">Agent</InputLabel>
                                                            <Select
                                                                labelId="agent-label"
                                                                id="agent-select"
                                                                label="Agent"
                                                                value={selectedAgent}
                                                                onChange={(e) => setSelectedAgent(e.target.value)}
                                                            >
                                                                {agents.length === 0 && (
                                                                    <MenuItem value="" disabled>
                                                                        No Active Agents
                                                                    </MenuItem>
                                                                )}
                                                                {agents.map((agent) => (
                                                                    <MenuItem key={agent.id} value={agent.id}>
                                                                        {agent.name}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Account ID"
                                                            value={accountId}
                                                            onChange={(e) => setAccountId(e.target.value)}
                                                        />
                                                        <FormControl fullWidth size="small">
                                                            <InputLabel id="cred-label">Select Credential</InputLabel>
                                                            <Select
                                                                labelId="cred-label"
                                                                value={credId}
                                                                label="Select Credential"
                                                                onChange={(e) => {
                                                                    setCredId(e.target.value);
                                                                    setIsCreatingCredential(e.target.value === "__create_new__");
                                                                }}
                                                                renderValue={(val: any) => {
                                                                    if (!val) return "";
                                                                    if (val === "__create_new__") return "Create New Credential";
                                                                    const cred = credentials.find((c) => c.id === val);
                                                                    return cred?.name || cred?.cred_name || String(val);
                                                                }}
                                                                MenuProps={{
                                                                    PaperProps: {
                                                                        style: { maxHeight: 320 },
                                                                        className: "scroll",
                                                                    },
                                                                }}
                                                            >
                                                                <MenuItem value="__create_new__">
                                                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                                                        <Box component="span" sx={{ color: "#3a2ada", fontSize: 20, display: 'flex', alignItems: 'center' }}>+</Box>
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary="Create New Credential"
                                                                        primaryTypographyProps={{ sx: { color: "#3a2ada", fontWeight: 600, fontSize: 14 } }}
                                                                    />
                                                                </MenuItem>
                                                                {credentials.map((cred) => (
                                                                    <MenuItem key={cred.id} value={cred.id}>{cred.name || cred.cred_name}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Box>
                                                </Box>

                                                {isCreatingCredential && (
                                                    <Box sx={{ p: 2, border: '1px solid #E0E0E0', borderRadius: 2, backgroundColor: '#FAFAFA', width: '100%' }}>
                                                        <Typography sx={{ mb: 2, fontWeight: 600, fontSize: 14 }}>Create New Credential</Typography>
                                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                                                <TextField
                                                                    label="Credential Name *"
                                                                    value={newCredName}
                                                                    onChange={(e) => setNewCredName(e.target.value)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                />
                                                                <TextField
                                                                    label="Description (Optional)"
                                                                    value={newCredDescription}
                                                                    onChange={(e) => setNewCredDescription(e.target.value)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                />
                                                                <TextField
                                                                    label="Access Key *"
                                                                    value={newCredAccessKey}
                                                                    onChange={(e) => setNewCredAccessKey(e.target.value)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                />
                                                                <TextField
                                                                    label="Secret Key *"
                                                                    value={newCredSecretKey}
                                                                    onChange={(e) => setNewCredSecretKey(e.target.value)}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                />
                                                            </Box>
                                                        </Box>
                                                    )}

                                                    {/* <Box sx={{ flex: 1, minWidth: 200 }}>
                                                    <FormControl
                                                        fullWidth
                                                        size="small"
                                                    >
                                                        <InputLabel id="asset-label">Assets</InputLabel>
                                                        <Select
                                                            labelId="asset-label"
                                                            id="asset-select"
                                                            label="Assets"
                                                            multiple
                                                            value={
                                                                selectedAssets.length === 0
                                                                    ? []
                                                                    : selectedAssets
                                                            }
                                                            open={assetDropdownOpen}
                                                            onClose={() =>
                                                                setAssetDropdownOpen(false)
                                                            }
                                                            onOpen={() =>
                                                                setAssetDropdownOpen(true)
                                                            }
                                                            renderValue={(selected) => {
                                                                if (
                                                                    selected.includes("All") ||
                                                                    selected.length ===
                                                                    assetOptions.length
                                                                ) {
                                                                    return "All";
                                                                }
                                                                if (selected.length > 3) {
                                                                    const firstThree = selected.slice(0, 3).join(", ");
                                                                    const remainingCount = selected.length - 3;
                                                                    return `${firstThree} +${remainingCount}`;
                                                                }
                                                                return selected.join(", ");
                                                            }}
                                                            displayEmpty
                                                            sx={{
                                                                "& .MuiSelect-select": {
                                                                    py: 1,
                                                                },
                                                            }}
                                                        >
                                                            {assetOptions.map((asset) => (
                                                                <MenuItem
                                                                    key={asset}
                                                                    value={asset}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAssetChange(asset);
                                                                    }}
                                                                >
                                                                    <Checkbox
                                                                        checked={selectedAssets.includes(
                                                                            asset
                                                                        )}
                                                                    />
                                                                    <ListItemText primary={asset} />
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Box> */}

                                                {/* instructions */}
                                                <Box sx={{ mt: 2 }}>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "space-between",
                                                            mb: 1,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 500 }}
                                                        >
                                                            Create Rule
                                                        </Typography>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, }}>

                                                            <WhiteTooltip
                                                                placement="auto-start"
                                                                title={
                                                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, }}>
                                                                        <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                                                                            Instructions
                                                                        </Typography>

                                                                        {awsInstructionGuidelines.map((line, index) => (
                                                                            <Typography
                                                                                key={line}
                                                                                variant="caption"
                                                                                component="div"
                                                                                sx={{
                                                                                    display: "flex",
                                                                                    alignItems: "flex-start",
                                                                                    color: secondaryTextColor,
                                                                                    fontSize: "12px",
                                                                                }}
                                                                            >
                                                                                <span style={{ marginRight: 6 }}>•</span>
                                                                                <span>{line}</span>
                                                                            </Typography>
                                                                        ))}
                                                                    </Box>
                                                                }
                                                            >
                                                                <InfoIcon
                                                                    sx={{
                                                                        fontSize: "18px",
                                                                        cursor: "default",
                                                                        color: "#696595",
                                                                    }}
                                                                />
                                                            </WhiteTooltip>

                                                            <Typography sx={{ fontSize: "12px", color: "#696595" }}>
                                                                Instructions
                                                            </Typography>

                                                        </Box>
                                                    </Box>
                                                    <div style={{ overflowY: "auto" }} className="scroll">
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            rows={4}
                                                            placeholder="Type Instruction here."
                                                            value={instruction}
                                                            onChange={(e) =>
                                                                handleInstructionInputChange(e.target.value)
                                                            }
                                                            error={Boolean(instructionError)}
                                                            helperText={
                                                                instructionError ||
                                                                (instructionCharCount >= MIN_INSTRUCTION_CHARS
                                                                    ? `${instructionCharCount} / ${MAX_INSTRUCTION_CHARS} characters`
                                                                    : "")
                                                            }
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": {
                                                                    padding: 0,
                                                                },
                                                            }}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1 }}>
                                                                        <IconButton disabled={!instruction || instruction.trim() === ""} onClick={handleCopy} sx={{ p: 0, pr: 1 }}>
                                                                            {isCopied ? (
                                                                                <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>Copied!</Typography>
                                                                            ) : (
                                                                                <ContentCopyIcon fontSize="small" />
                                                                            )}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                        />
                                                    </div>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        mt: 1.5,
                                                    }}
                                                >
                                                    {/* Left Side: Navigation */}
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleNavigate('prev')}
                                                            disabled={currentHistoryIndex <= 0}
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            <ArrowBackIcon fontSize="small" color={currentHistoryIndex > 0 ? "primary" : "disabled"} />
                                                        </IconButton>

                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", fontSize: "12px" }}>
                                                            {testHistory.length > 0 ? `${currentHistoryIndex + 1} / ${testHistory.length}` : "0 / 0"}
                                                        </Typography>

                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleNavigate('next')}
                                                            disabled={currentHistoryIndex >= testHistory.length - 1}
                                                            sx={{ p: 0.5 }}
                                                        >
                                                            <ArrowForwardIcon fontSize="small" color={currentHistoryIndex < testHistory.length - 1 ? "primary" : "disabled"} />
                                                        </IconButton>

                                                        <Typography variant="body2" sx={{ ml: 1, color: "text.secondary", fontSize: "12px", fontStyle: "italic" }}>
                                                            Previous Responses
                                                        </Typography>
                                                    </Box>

                                                    {/* Right Side */}
                                                    <Box sx={{ display: "flex", gap: 2 }}>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => {
                                                                setInstruction("");
                                                                setAccountId("");
                                                                setCredId("");
                                                                setIsCreatingCredential(false);
                                                                setNewCredName("");
                                                                setNewCredDescription("");
                                                                setNewCredAccessKey("");
                                                                setNewCredSecretKey("");
                                                            }}
                                                            sx={{
                                                                color: theme.palette.primary.main,
                                                                borderColor: theme.palette.primary.main,
                                                            }}
                                                        >
                                                            Reset
                                                        </Button>

                                                        <Button
                                                            variant="contained"
                                                            onClick={handleRunTests}
                                                            disabled={isLoading}
                                                            sx={{
                                                                backgroundColor: theme.palette.primary.main,
                                                                minWidth: "80px"
                                                            }}
                                                        >
                                                            {isLoading ? (
                                                                <CircularProgress size={20} color="inherit" />
                                                            ) : (
                                                                "Test"
                                                            )}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}

                                        {step.key === "test" && (
                                            <Box>
                                                {isLoading ? (
                                                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, flexDirection: "column", gap: 2 }}>
                                                        <CircularProgress size={40} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            Running compliance tests...
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                        {ruleTestResults.map((result, index) => {
                                                            const status = getStatusStyles(result.status);
                                                            return (
                                                                <Box
                                                                    key={result.id}
                                                                    sx={{
                                                                        borderRadius: "10px",
                                                                        boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: 1,
                                                                        }}
                                                                    >
                                                                        <Typography sx={{ fontWeight: 400, fontSize: "13px" }}>
                                                                            {result.command}
                                                                        </Typography>
                                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                                                                            {status?.icon}
                                                                            {result.hasDetails ? (
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={() => toggleTestDetails(index, result.hasDetails)}
                                                                                    sx={{
                                                                                        transition: "transform 0.2s",
                                                                                        transform:
                                                                                            expandedTestIndex === index
                                                                                                ? "rotate(180deg)"
                                                                                                : "rotate(0deg)",
                                                                                    }}
                                                                                >
                                                                                    <KeyboardArrowDownIcon />
                                                                                </IconButton>
                                                                            ) : (
                                                                                <IconButton size="small" disabled>
                                                                                    <KeyboardArrowDownIcon />
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                    {result.hasDetails && (
                                                                        <Collapse in={expandedTestIndex === index} unmountOnExit>
                                                                            <Box sx={{ pl: 1 }}>
                                                                                <Typography
                                                                                    variant="subtitle2"
                                                                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                                                                >
                                                                                    Reason:
                                                                                </Typography>
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    sx={{ color: "text.secondary", mb: 1.5 }}
                                                                                >
                                                                                    {result.reason}
                                                                                </Typography>
                                                                                {result.proof && (
                                                                                    <Box sx={{ mt: 2 }}>
                                                                                        <Box
                                                                                            sx={{
                                                                                                display: "flex",
                                                                                                alignItems: "center",
                                                                                                justifyContent: "space-between",
                                                                                                mb: 1,
                                                                                            }}
                                                                                        >
                                                                                            <Typography
                                                                                                variant="subtitle2"
                                                                                                sx={{ fontWeight: 600 }}
                                                                                            >
                                                                                                Proof:
                                                                                            </Typography>
                                                                                            <Button
                                                                                                size="small"
                                                                                                onClick={() => toggleProofSection(result.id)}
                                                                                                variant="outlined"
                                                                                                sx={{ textTransform: "none" }}
                                                                                            >
                                                                                                {proofExpanded[result.id] ? "Collapse" : "Expand"}
                                                                                            </Button>
                                                                                        </Box>
                                                                                        <Collapse in={!!proofExpanded[result.id]}>
                                                                                            <Box
                                                                                                sx={{
                                                                                                    p: 2,
                                                                                                    border: "1px solid",
                                                                                                    borderColor: "divider",
                                                                                                    borderRadius: 2,
                                                                                                    backgroundColor: subtleSurfaceColor,
                                                                                                    wordBreak: "break-word",    // 👈 break long words
                                                                                                    whiteSpace: "pre-wrap",     // 👈 allow wrapping
                                                                                                }}
                                                                                            >
                                                                                                <ReactJson
                                                                                                    src={result.proof}
                                                                                                    name={false}
                                                                                                    theme={isDarkMode ? "monokai" : "rjv-default"}
                                                                                                    collapsed={1}
                                                                                                    enableClipboard={false}
                                                                                                    displayDataTypes={false}
                                                                                                    displayObjectSize={false}
                                                                                                />
                                                                                            </Box>
                                                                                        </Collapse>
                                                                                    </Box>
                                                                                )}
                                                                            </Box>
                                                                        </Collapse>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        gap: 1.5,
                                                        mt: 3,
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        onClick={handleCreateRule}
                                                        disabled={isLoading}
                                                        sx={{
                                                            backgroundColor: theme.palette.primary.main,
                                                        }}
                                                    >
                                                        Create
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}

                                        {step.key === "details" && (
                                            <Box>
                                                <Box sx={{ flex: 1, mb: 2 }}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Rule Name"
                                                        value={ruleName}
                                                        onChange={(e) => handleRuleNameChange(e.target.value)}
                                                        error={Boolean(ruleNameError)}
                                                        helperText={ruleNameError || `${(ruleName || "").toString().trim().length} / 256 characters`}
                                                        // InputProps={{
                                                        //     endAdornment: (
                                                        //         <InputAdornment position="end">
                                                        //             <IconButton
                                                        //                 size="small"
                                                        //                 onClick={() => handleRuleNameChange("")}
                                                        //                 edge="end"
                                                        //             >
                                                        //                 <RefreshIcon
                                                        //                     sx={{ fontSize: "18px" }}
                                                        //                 />
                                                        //             </IconButton>
                                                        //         </InputAdornment>
                                                        //     ),
                                                        // }}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "8px",
                                                            },
                                                        }}
                                                    />
                                                </Box>

                                                <Box sx={{ flex: 1, mb: 2, }}>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        rows={4}
                                                        label="Description"
                                                        placeholder="Type Description here."
                                                        value={description}
                                                        onChange={(e) => handleDescriptionChange(e.target.value)}
                                                        error={Boolean(descriptionError)}
                                                        helperText={descriptionError}
                                                        inputProps={{
                                                            className: "scroll",
                                                        }}
                                                        // InputProps={{
                                                        //     endAdornment: (
                                                        //         <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 1 }}>
                                                        //             <IconButton
                                                        //                 size="small"
                                                        //                 onClick={() => handleDescriptionChange("")}
                                                        //                 edge="end"
                                                        //             >
                                                        //                 <RefreshIcon
                                                        //                     sx={{ fontSize: "18px" }}
                                                        //                 />
                                                        //             </IconButton>
                                                        //         </InputAdornment>
                                                        //     ),
                                                        // }}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "8px",
                                                            },
                                                        }}
                                                    />
                                                </Box>

                                                <Box sx={{ flex: 1, mb: 2 }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ mb: 1, fontWeight: 500 }}
                                                    >
                                                        Remediation :
                                                    </Typography>
                                                    {/* <FormControl
                                                    fullWidth
                                                    size="small"
                                                    sx={{ mb: 2 }}
                                                >
                                                    <InputLabel>Applicable Assets</InputLabel>
                                                    <Select
                                                        value={applicableAsset}
                                                        label="Applicable Assets"
                                                        onChange={(e) =>
                                                            setApplicableAsset(e.target.value)
                                                        }
                                                        sx={{
                                                            borderRadius: "8px",
                                                        }}
                                                    >
                                                        <MenuItem value="Cisco IOS">
                                                            Cisco IOS
                                                        </MenuItem>
                                                        <MenuItem value="Cisco NX-OS">
                                                            Cisco NX-OS
                                                        </MenuItem>
                                                        <MenuItem value="Juniper">
                                                            Juniper
                                                        </MenuItem>
                                                    </Select>
                                                </FormControl> */}
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        rows={6}
                                                        placeholder="Type Remediation here."
                                                        value={remediation}
                                                        onChange={(e) => handleRemediationChange(e.target.value)}
                                                        error={Boolean(remediationError)}
                                                        helperText={remediationError}
                                                        inputProps={{
                                                            className: "scroll",
                                                        }}
                                                        // InputProps={{
                                                        //     endAdornment: (
                                                        //         <InputAdornment position="end" sx={{ alignSelf: "flex-end", mb: 1 }}>
                                                        //             <IconButton
                                                        //                 size="small"
                                                        //                 onClick={() => handleRemediationChange("")}
                                                        //                 edge="end"
                                                        //             >
                                                        //                 <RefreshIcon
                                                        //                     sx={{ fontSize: "18px" }}
                                                        //                 />
                                                        //             </IconButton>
                                                        //         </InputAdornment>
                                                        //     ),
                                                        // }}
                                                        sx={{
                                                            "& .MuiOutlinedInput-root": {
                                                                borderRadius: "8px",
                                                            },
                                                        }}
                                                    />
                                                </Box>

                                                <Box sx={{ flex: 1, mb: 2 }}>
                                                    <Typography
                                                        variant="subtitle1"
                                                        sx={{ fontWeight: 600, fontSize: "13px" }}
                                                    >
                                                        Severity
                                                    </Typography>
                                                    <Box sx={{ px: 0 }}>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: 2,
                                                            }}
                                                        >
                                                            <Slider
                                                                value={severityValue}
                                                                onChange={(_, newValue) =>
                                                                    setSeverityValue(newValue as number)
                                                                }
                                                                min={0.1}
                                                                max={10}
                                                                step={0.1}
                                                                sx={{
                                                                    flex: 1,
                                                                    color: getSeverityColor(severityValue),
                                                                    "& .MuiSlider-thumb": {
                                                                        backgroundColor: getSeverityColor(
                                                                            severityValue
                                                                        ),
                                                                    },
                                                                    "& .MuiSlider-track": {
                                                                        backgroundColor: getSeverityColor(
                                                                            severityValue
                                                                        ),
                                                                    },
                                                                }}
                                                            />
                                                            <span
                                                                style={{ fontWeight: 600, minWidth: "30px", fontSize: "13px" }}
                                                            >
                                                                {severityValue.toFixed(1)}
                                                            </span>
                                                            <Chip
                                                                label={getSeverityLabel(severityValue)}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: `${getSeverityColor(severityValue)}1A`,
                                                                    color: getSeverityColor(severityValue),
                                                                    fontWeight: 600,
                                                                    borderRadius: "16px",
                                                                    minWidth: "70px",
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        gap: 1.5,
                                                        mt: 3,
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        disabled={!isRulesTestCompleted || !isDeployValid || isDeploying || hasPendingResults}
                                                        onClick={handleDeploy}
                                                        sx={{
                                                            textTransform: "none",
                                                            borderRadius: "8px",
                                                            backgroundColor: theme.palette.primary.main,
                                                            fontWeight: 600,
                                                            px: 3,
                                                            "&.Mui-disabled": {
                                                                backgroundColor: theme.palette.action.disabledBackground,
                                                                color: theme.palette.text.disabled,
                                                            },
                                                        }}
                                                    >
                                                        {isDeploying ? <CircularProgress size={20} color="inherit" /> : "Deploy"}
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Collapse>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Confirmation Dialog */}
            <Dialog
                open={showDraftConfirm}
                onClose={() => setShowDraftConfirm(false)}
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        padding: "8px",
                        minWidth: "300px"
                    }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600, fontSize: "16px", textAlign: "center" }}>
                    Confirm Action
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: "14px", textAlign: "center", color: "text.secondary" }}>
                        Do you want to move this to draft?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={async () => {
                            if (isSavingDraftRef.current) return;
                            isSavingDraftRef.current = true;
                            setIsSavingDraft(true);
                            try {
                                const payload = {
                                    organization_id: (organization_id || "").trim(),
                                    account_id: (accountId || "").trim(),
                                    agent_id: (socketAgentId || selectedAgent || "").trim(),
                                    control_name: (ruleName || instruction || "").trim(),
                                    user_input: (instruction || ruleName || "").trim(),
                                    description: (description || "").trim(),
                                    remediation: (remediation || "").trim(),
                                    cvss_vector: (cvssVector || complianceResults?.control_info?.cvss_vector || complianceTaskData?.control_info?.cvss_vector || editData?.cvss_vector || editData?.control_info?.cvss_vector || "").trim(),
                                    cvss_score: severityValue || complianceResults?.control_info?.cvss_score || complianceTaskData?.control_info?.cvss_score || editData?.cvss_score || editData?.control_info?.cvss_score || 0.1,
                                    vendor: (socketVendor || "AWS").trim(),
                                    rule_type: (editData?.rule_type || "custom").trim(),
                                    status: "draft",
                                    ...(ruleUuid ? { rule_id: (ruleUuid || "").trim(), rule_uuid: (ruleUuid || "").trim() } : {}),
                                    details: {
                                        compliance_results: complianceResults || complianceTaskData?.compliance_results || editData?.details?.compliance_results || {},
                                        result_count: complianceResults?.result_count ?? complianceTaskData?.result_count ?? editData?.details?.result_count ?? ruleTestResults.length,
                                        processing_completed_at: complianceResults?.processing_completed_at || complianceTaskData?.processing_completed_at || editData?.details?.processing_completed_at || "",
                                        control_info: complianceResults?.control_info || complianceTaskData?.control_info || editData?.details?.control_info || {},
                                        ruleTestResults: ruleTestResults.map((result) => ({
                                            command: (result.command || "").trim(),
                                            reason: (result.reason || "").trim(),
                                            proof: typeof result.proof === "string" ? result.proof.trim() : (result.proof || ""),
                                            status_icon: (result.status || "").trim(),
                                        })),
                                    }
                                };

                                await axiosInstance.post(END_POINTS.RULE_MANAGEMENT_DEPLOY, payload);
                                toast.success("Moved to draft successfully.");
                                onRuleAdded?.();
                                setShowDraftConfirm(false);
                                onClose();
                            } catch (error: any) {
                                console.error("Error saving draft:", error);
                                toast.error("Failed to move to draft.");

                            } finally {
                                isSavingDraftRef.current = false;
                                setIsSavingDraft(false);
                            }
                        }}
                        disabled={isSavingDraft}
                        sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            fontWeight: 600,
                            px: 3,
                            pointerEvents: isSavingDraft ? "none" : "auto",
                        }}
                    >
                        {isSavingDraft ? <CircularProgress size={18} color="inherit" /> : "Yes"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShowDraftConfirm(false);
                            onClose();
                        }}
                        sx={{
                            textTransform: "none",
                            borderRadius: "8px",
                            fontWeight: 600,
                            px: 3
                        }}
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </Drawer>
    );
};

export default AddRuleDrawer;

